import { useMemo } from 'react';
import { toMonthly } from '../utils/frequency';

function sumMonthly(items) {
  return items.reduce((sum, item) => sum + toMonthly(item.amount || 0, item.frequency || 'monthly'), 0);
}

function groupByCategory(items) {
  const groups = {};
  for (const item of items) {
    const cat = item.category || 'Otros';
    if (!groups[cat]) groups[cat] = { items: [], total: 0 };
    const monthly = toMonthly(item.amount || 0, item.frequency || 'monthly');
    groups[cat].items.push({ ...item, monthlyAmount: monthly });
    groups[cat].total += monthly;
  }
  return groups;
}

export function useDerivedState(state) {
  return useMemo(() => {
    const monthlyIncome = sumMonthly(state.incomeSources);
    const monthlyFixedExpenses = sumMonthly(state.fixedExpenses);
    const monthlyVariableExpenses = sumMonthly(state.variableExpenses);
    const monthlyTotalExpenses = monthlyFixedExpenses + monthlyVariableExpenses;
    const monthlyNet = monthlyIncome - monthlyTotalExpenses;

    const savingsRate = monthlyIncome > 0 ? (monthlyNet / monthlyIncome) * 100 : 0;

    // Housing ratio: rent/mortgage as % of income
    const housingCategories = ['Vivienda', 'Housing', 'Logement', 'Alquiler', 'Rent', 'Loyer', 'Hipoteca', 'Mortgage', 'Hypothèque'];
    const housingExpenses = [...state.fixedExpenses, ...state.variableExpenses].filter((e) =>
      housingCategories.some((cat) => (e.category || '').toLowerCase().includes(cat.toLowerCase()) || (e.name || '').toLowerCase().includes(cat.toLowerCase()))
    );
    const monthlyHousing = sumMonthly(housingExpenses);
    const housingRatio = monthlyIncome > 0 ? (monthlyHousing / monthlyIncome) * 100 : 0;

    // Debt-to-income
    const monthlyDebtPayments = state.debts.reduce((sum, d) => sum + (d.minPayment || 0), 0);
    const debtToIncomeRatio = monthlyIncome > 0 ? (monthlyDebtPayments / monthlyIncome) * 100 : 0;
    const totalDebt = state.debts.reduce((sum, d) => sum + (d.balance || 0), 0);

    // Emergency fund months (savings / monthly expenses)
    const currentEmergencyMonths = monthlyTotalExpenses > 0 ? Math.max(0, monthlyNet * 6) / monthlyTotalExpenses : 0;

    // Category breakdowns
    const fixedByCategory = groupByCategory(state.fixedExpenses);
    const variableByCategory = groupByCategory(state.variableExpenses);

    // Grocery spending (for insights)
    const groceryCategories = ['Comida', 'Groceries', 'Alimentation', 'Supermercado', 'Alimentos'];
    const groceryExpenses = state.variableExpenses.filter((e) =>
      groceryCategories.some((cat) => (e.category || '').toLowerCase().includes(cat.toLowerCase()) || (e.name || '').toLowerCase().includes(cat.toLowerCase()))
    );
    const monthlyGroceries = sumMonthly(groceryExpenses);

    return {
      monthlyIncome,
      monthlyFixedExpenses,
      monthlyVariableExpenses,
      monthlyTotalExpenses,
      monthlyNet,
      savingsRate,
      housingRatio,
      monthlyHousing,
      debtToIncomeRatio,
      monthlyDebtPayments,
      totalDebt,
      currentEmergencyMonths,
      fixedByCategory,
      variableByCategory,
      monthlyGroceries,
    };
  }, [state.incomeSources, state.fixedExpenses, state.variableExpenses, state.debts]);
}
